import React from "react";

export type QuickStartTaskId =
	| "quickstart-dentist-connected"
	| "quickstart-lab-connected"
	| "quickstart-file-sent-chat"
	| "quickstart-email-branding"
	| "quickstart-form-created"
	| "quickstart-dentist-used-form"
	| "quickstart-lab-used-form"
	| "quickstart-form-on-website"
	| "quickstart-form-rules"
	| "quickstart-rule-created"
	| "quickstart-jumbo-created"
	| "quickstart-jumbo-used"
	| "quickstart-custom-view-created";

export interface QuickstartTask {
	id: QuickStartTaskId;
	title: string;
	content: React.ReactNode;
	cta?: {
		label: string;
		to: string;
	};
}

export type QuickStartSectionId =
	| "quickstart-kommunikation"
	| "quickstart-digitale-auftragsblatter"
	| "quickstart-jumbos"
	| "quickstart-auftragsansichten";

export interface QuickstartSection {
	id: QuickStartSectionId;
	title: string;
	order: number;
	tasks: QuickstartTask[];
}

export const QUICKSTART_TASKS: QuickstartSection[] = [
	{
		id: "quickstart-kommunikation",
		title: "Kommunikation",
		order: 1,
		tasks: [
			{
				id: "quickstart-dentist-connected",
				title: "Zahnarzt für Kommunikation verknüpft",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Verknüpfen Sie einen Zahnarzt, um die Kommunikation
							über dentCONNECT zu nutzen.
						</p>
					</div>
				),
			},
			{
				id: "quickstart-lab-connected",
				title: "Labor für Kommunikation verknüpft",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Verknüpfen Sie ein Fremdlabor, um Kommunikation und
							Datenaustausch zu ermöglichen.
						</p>
					</div>
				),
			},
			{
				id: "quickstart-file-sent-chat",
				title: "Datei über Chat versendet",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Testen Sie die Chat-Funktion, indem Sie eine Datei
							an einen Zahnarzt oder ein Labor senden.
						</p>
					</div>
				),
				cta: { label: "Zum Chat", to: "/chats" },
			},
			/* 			{
				id: "quickstart-email-branding",
				title: "Eigenes E-Mail Branding erstellt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Passen Sie Ihr E-Mail Branding an, um professionelle E-Mails mit Ihrem Logo zu versenden.
						</p>
					</div>
				),
			}, */
		],
	},
	{
		id: "quickstart-digitale-auftragsblatter",
		title: "Digitale Auftragsblätter",
		order: 2,
		tasks: [
			{
				id: "quickstart-form-created",
				title: "Auftragsformular erstellt und veröffentlicht",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Erstellen Sie Ihr erstes digitales Auftragsformular
							und veröffentlichen Sie es.
						</p>
					</div>
				),
				cta: {
					label: "Formular erstellen",
					to: "/auftragsformulare",
				},
			},
			{
				id: "quickstart-dentist-used-form",
				title: "Zahnarzt hat Auftragsformular genutzt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Ein Zahnarzt hat Ihr Auftragsformular ausgefüllt und
							einen Auftrag übermittelt und sie haben akzeptiert.
						</p>
					</div>
				),
			},
			{
				id: "quickstart-lab-used-form",
				title: "Labor hat Auftragsformular genutzt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Ein Fremdlabor hat Ihr Auftragsformular genutzt und
							sie haben akzeptiert.
						</p>
					</div>
				),
			},
			/* 			{
				id: "quickstart-form-on-website",
				title: "Formular auf Website hinterlegt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Binden Sie Ihr Auftragsformular in Ihre Website ein, damit Kunden direkt Aufträge erstellen können.
						</p>
					</div>
				),
			}, */
			{
				id: "quickstart-rule-created",
				title: "Regel erstellt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Sie haben Ihre erste Automatisierungs-Regel für
							Auftragsformulare erstellt.
						</p>
					</div>
				),
				cta: {
					label: "Regel erstellen",
					to: "/auftragsformulare",
				},
			},
		],
	},
	{
		id: "quickstart-jumbos",
		title: "Jumbos",
		order: 3,
		tasks: [
			{
				id: "quickstart-jumbo-created",
				title: "Jumbo erstellt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Erstellen Sie eine Jumbo-Vorlage, um wiederkehrende
							Aufträge effizienter zu bearbeiten.
						</p>
					</div>
				),
				cta: {
					label: "Jumbo erstellen",
					to: "/organisation/vorlagen-jumbos",
				},
			},
			{
				id: "quickstart-jumbo-used",
				title: "Jumbo genutzt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Verwenden Sie Ihre Jumbo-Vorlage bei einem Auftrag.
						</p>
					</div>
				),
			},
		],
	},
	{
		id: "quickstart-auftragsansichten",
		title: "Auftragsansichten",
		order: 4,
		tasks: [
			{
				id: "quickstart-custom-view-created",
				title: "Eigene Auftragsansicht erstellt und hinterlegt",
				content: (
					<div className="space-y-2">
						<p className="text-sm text-gray-600">
							Passen Sie Ihre Auftragsansicht an Ihre Bedürfnisse
							an und speichern Sie die Konfiguration.
						</p>
					</div>
				),
				cta: {
					label: "Ansicht erstellen",
					to: "/organisation/auftragsansichten",
				},
			},
		],
	},
];

export const TOTAL_QUICKSTART_TASKS = QUICKSTART_TASKS.reduce(
	(acc, section) => acc + section.tasks.length,
	0
);
export const SETUP_TASKS_COUNT = 5;
